// Shared video player for Career Central career and cluster pages.
//
// Two presentations, both backed by Shaka playing the video platform (DASH):
//   - The primary career/cluster video plays INLINE in its .video-container.
//   - Q&A carousel videos render as poster buttons (.career-video-trigger);
//     clicking one opens a modal and plays it there — matching the popover
//     behavior the old Wistia integration provided.
//
// It also ports the two cross-cutting behaviors from Wistia: percent-watched
// tracking (25/50/75/100) and single-video playback (starting one pauses others).
Postlogin.CareerCentralVideo = (function () {
    const me = {};

    // Percent thresholds that get reported to the server, in ascending order.
    const PERCENT_THRESHOLDS = [
        { fraction: 0.25, percent: 25 },
        { fraction: 0.5, percent: 50 },
        { fraction: 0.75, percent: 75 },
        { fraction: 0.98, percent: 100 },
    ];

    // Inline Shaka <video> elements (the primary video), for cross-player pause.
    let shakaVideoElements = [];
    // The Shaka player currently mounted in the Q&A modal, so it can be torn
    // down when the modal closes (freeing the media session and its token).
    let modalPlayer = null;

    // Pause every OTHER inline player so only one video plays at a time.
    const pauseOthers = function (currentKey) {
        shakaVideoElements.forEach((el) => {
            if (el.getAttribute('data-video-key') !== currentKey && !el.paused) {
                el.pause();
            }
        });
    };

    /**
     * Initialize Career Central videos on the page.
     *
     * @param {object} options
     * @param {string} options.videoType     'Job Shadow' or 'Cluster Overview'
     * @param {string} options.pageLoadId    Random id grouping views from one page load
     * @param {function} options.onPercentWatched  Called with a videoData object when a threshold is crossed
     */
    me.init = function (options) {
        shakaVideoElements = [];

        // Primary video: play inline.
        $('video.shaka-video').each(function () {
            initInlineVideo(this, options);
        });

        // Q&A videos: poster buttons that open the modal player.
        $('body').off('click.ccvideo').on('click.ccvideo', '.career-video-trigger', function () {
            openModal(this, options);
        });

        // Tear the modal player down whenever the modal closes.
        $('#career-video-modal').off('hidden.bs.modal.ccvideo').on('hidden.bs.modal.ccvideo', teardownModal);
    };

    // --- Shaka core ---

    // Attach a Shaka player to a <video>, authenticate manifest/segment
    // requests with the JWT, mount the UI overlay, and load the manifest.
    // Returns the player, or null if Shaka is unavailable/unsupported.
    //
    // The request filter reads the token from player.tokenState (a mutable
    // box, not the videoToken argument directly) so switchTrack can update
    // player.tokenState.token and have the NEXT manifest/segment request
    // carry the new track's JWT, without re-registering a filter.
    const startShaka = function (videoElement, manifestUrl, videoToken) {
        if (typeof shaka === 'undefined') {
            console.error('Shaka Player library not loaded');
            return null;
        }
        shaka.polyfill.installAll();
        if (!shaka.Player.isBrowserSupported()) {
            console.error('Shaka Player not supported in this browser');
            return null;
        }
        if (!manifestUrl) {
            console.error('No manifest URL for Career Central video');
            return null;
        }

        const player = new shaka.Player();
        player.tokenState = { token: videoToken };
        player.loadPromise = player.attach(videoElement).then(() => {
            player.getNetworkingEngine().registerRequestFilter((type, request) => {
                if (player.tokenState.token) {
                    // Shaka's request filter API requires mutating the request in place.
                    // eslint-disable-next-line no-param-reassign
                    request.headers.Authorization = `Bearer ${player.tokenState.token}`;
                }
            });
            // Shaka UI overlay provides the player chrome (seekbar, play,
            // volume, quality menu, fullscreen) Wistia used to render.
            /* global shaka */
            // eslint-disable-next-line no-new
            new shaka.ui.Overlay(player, videoElement.parentElement, videoElement);
            return player.load(manifestUrl);
        });
        return player;
    };

    // Switch a live player to a different language/audio track: point the
    // request filter at the new track's JWT, then reload the same player
    // against the new track's manifest (each track is its own separate
    // single-language manifest on the video platform, not multiple audio
    // AdaptationSets in one manifest, so a plain player.load() is required).
    // Resumes at the same point and play state instead of restarting from
    // 0:00 -- player.load()'s startTime param does the seek; ShakaPlayer.tsx
    // (video-mfe) does the same capture-then-resume around its load() call.
    const switchTrack = function (videoElement, player, track) {
        const resumeTime = videoElement.currentTime;
        const wasPlaying = !videoElement.paused;
        // player.tokenState is a mutable box by design (see startShaka) so the
        // request filter picks up the new token on the next request.
        // eslint-disable-next-line no-param-reassign
        player.tokenState.token = track.token;
        player.load(track.manifest_url, resumeTime).then(() => {
            if (wasPlaying) {
                const p = videoElement.play();
                if (p && typeof p.catch === 'function') {
                    p.catch(() => {});
                }
            }
        }).catch((err) => {
            console.error('Shaka track-switch load error', err);
        });
    };

    // Parse a video/trigger element's data-tracks attribute (a JSON array
    // from VideoService::buildTrackOptions) into a track list, or [].
    const parseTracks = function (raw) {
        if (!raw) {
            return [];
        }
        try {
            const tracks = JSON.parse(raw);
            return Array.isArray(tracks) ? tracks : [];
        } catch (e) {
            return [];
        }
    };

    // Build the language/track overlay (label + <select>), or null when
    // there's nothing to switch between (zero or one track — the common
    // case). Hides itself while the video is playing and reappears on
    // pause, matching @pathful/video-player's LanguageSelector (video-mfe)
    // so both players share the same chrome.
    const buildTrackSwitcher = function (videoElement, tracks, currentManifestUrl, player) {
        if (tracks.length < 2) {
            return null;
        }
        const $wrapper = $('<div class="video-language-selector"></div>');
        $('<label></label>').text('Language:').appendTo($wrapper);
        const $select = $('<select></select>').attr('aria-label', 'Select video language');
        tracks.forEach((track) => {
            $('<option></option>')
                .val(track.manifest_url)
                .text(track.label)
                .prop('selected', track.manifest_url === currentManifestUrl)
                .appendTo($select);
        });
        $select.on('change', function () {
            const selected = tracks.find((t) => t.manifest_url === $(this).val());
            if (selected) {
                switchTrack(videoElement, player, selected);
            }
        });
        $wrapper.append($select);

        const togglePlaying = () => $wrapper.toggleClass('is-playing', !videoElement.paused);
        videoElement.addEventListener('play', togglePlaying);
        videoElement.addEventListener('pause', togglePlaying);
        videoElement.addEventListener('ended', togglePlaying);

        return $wrapper;
    };

    // Mount (or replace) the language switcher as a corner overlay inside
    // $container (a position: relative ancestor of the video — .video-container
    // or .modal-video-container).
    const mountTrackSwitcher = function ($container, videoElement, player, tracks, currentManifestUrl) {
        $container.find('.video-language-selector').remove();
        const $wrapper = buildTrackSwitcher(videoElement, tracks, currentManifestUrl, player);
        if ($wrapper) {
            $container.append($wrapper);
        }
    };

    // Report percent watched as playback crosses each threshold, at most once
    // each; on a forward seek past several, report only the highest crossed.
    const wireShakaTracking = function (videoElement, videoData, options) {
        videoElement.addEventListener('play', () => pauseOthers(videoData.video_key));

        let reportedFraction = 0;
        videoElement.addEventListener('timeupdate', () => {
            if (!videoElement.duration || Number.isNaN(videoElement.duration)) {
                return;
            }
            const fraction = videoElement.currentTime / videoElement.duration;
            let crossed = null;
            PERCENT_THRESHOLDS.forEach((threshold) => {
                if (fraction >= threshold.fraction && reportedFraction < threshold.fraction) {
                    reportedFraction = threshold.fraction;
                    crossed = threshold.percent;
                }
            });
            if (crossed !== null) {
                options.onPercentWatched({ ...videoData, percent: crossed });
            }
        });
    };

    const videoDataFrom = function (el, options) {
        return {
            video_key: el.getAttribute('data-video-key'),
            percent: null,
            display_name: el.getAttribute('data-display-name'),
            qa_id: el.getAttribute('data-qa-id'),
            page_load_id: options.pageLoadId,
            video_type: options.videoType,
        };
    };

    // --- Inline (primary) video ---

    const initInlineVideo = function (videoElement, options) {
        const manifestUrl = videoElement.getAttribute('data-manifest-url');
        const videoToken = videoElement.getAttribute('data-video-token');
        const tracks = parseTracks(videoElement.getAttribute('data-tracks'));
        const player = startShaka(videoElement, manifestUrl, videoToken);
        if (!player) {
            showInlineError(videoElement);
            return;
        }
        player.loadPromise.then(() => {
            shakaVideoElements.push(videoElement);
            wireShakaTracking(videoElement, videoDataFrom(videoElement, options), options);
            mountTrackSwitcher($(videoElement).closest('.video-container'), videoElement, player, tracks, manifestUrl);
        }).catch((err) => {
            console.error('Shaka load error', err);
            showInlineError(videoElement);
        });
    };

    const showInlineError = function (videoElement) {
        $(videoElement).replaceWith(
            $('<div class="video-error"></div>').text('This video is currently unavailable.'),
        );
    };

    // --- Q&A modal video ---

    const openModal = function (trigger, options) {
        const $modal = $('#career-video-modal');
        const container = $modal.find('.modal-video-container').empty()[0];
        $modal.find('.modal-title').text(trigger.getAttribute('data-display-name') || '');

        const videoEl = document.createElement('video');
        videoEl.className = 'shaka-video';
        videoEl.setAttribute('playsinline', '');
        // Carry the identity onto the element so tracking reports the right video.
        ['data-video-key', 'data-qa-id', 'data-display-name'].forEach((attr) => {
            const v = trigger.getAttribute(attr);
            if (v !== null) videoEl.setAttribute(attr, v);
        });
        container.appendChild(videoEl);

        // Pause the inline primary video so it doesn't keep playing behind the modal.
        shakaVideoElements.forEach((el) => {
            if (!el.paused) el.pause();
        });

        $modal.modal('show');

        const manifestUrl = trigger.getAttribute('data-manifest-url');
        const videoToken = trigger.getAttribute('data-video-token');
        const tracks = parseTracks(trigger.getAttribute('data-tracks'));
        const player = startShaka(videoEl, manifestUrl, videoToken);
        if (!player) {
            $(container).html('<div class="video-error">This video is currently unavailable.</div>');
            return;
        }
        modalPlayer = player;
        player.loadPromise.then(() => {
            wireShakaTracking(videoEl, videoDataFrom(videoEl, options), options);
            mountTrackSwitcher($(container), videoEl, player, tracks, manifestUrl);
            const p = videoEl.play();
            if (p && typeof p.catch === 'function') {
                // Autoplay may be blocked; the user can press play in the overlay.
                p.catch(() => {});
            }
        }).catch((err) => {
            console.error('Shaka load error', err);
            $(container).html('<div class="video-error">This video is currently unavailable.</div>');
        });
    };

    const teardownModal = function () {
        if (modalPlayer) {
            modalPlayer.destroy();
            modalPlayer = null;
        }
        // The language switcher is mounted as a child of .modal-video-container,
        // so emptying it also removes the switcher.
        $('#career-video-modal').find('.modal-video-container').empty();
    };

    return me;
}());
